import { useState, useEffect, useRef } from 'react';
import {
  SentimentDataByPeriod,
  SentimentDays,
  UseSentimentResult,
} from '../../types/sentiment.type';
import { getSentimentData } from '../../api/sentiment.api';
import { errorMsg } from '../../constants/common.constant';
import { WidgetError, WidgetMessage } from '../../types/common.type';
import { WidgetMessageType } from '../../utils/type';

const SENTIMENT_DAYS: SentimentDays[] = ['1', '7', '30', '90'];

const mappingSentimentData: Record<
  SentimentDays,
  {
    interval: string;
    days: string;
  }
> = {
  '1': { interval: '30', days: '1' },
  '7': { interval: '1', days: '7' },
  '30': { interval: '1', days: '30' },
  '90': { interval: '7', days: '90' },
};
const useSentiment = (
  ric: string = '',
  token: string = '',
  onError?: (error: WidgetError) => void,
  onNoData?: (message: WidgetMessage) => void,
): UseSentimentResult => {
  const [data, setData] = useState<SentimentDataByPeriod>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const onErrorRef = useRef(onError);
  const onNoDataRef = useRef(onNoData);
  onErrorRef.current = onError;
  onNoDataRef.current = onNoData;

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    Promise.allSettled(
      SENTIMENT_DAYS.map((days) =>
        getSentimentData({ token, ric, ...mappingSentimentData[days] }),
      ),
    )
      .then((results) => {
        if (cancelled) return;

        const combined: SentimentDataByPeriod = {} as any;
        SENTIMENT_DAYS.forEach((day, index) => {
          const result = results[index];
          if (result.status === 'fulfilled') {
            const res = result.value;
            if (res?.data && res.data.overall) {
              combined[day] = res.data;
            }
          }
        });

        if (Object.keys(combined).length === 0) {
          onNoDataRef.current?.({
            type: WidgetMessageType.NoData,
            source: 'SentimentWidget',
            data: null,
          });
          throw new Error();
        }

        setData(combined);
      })
      .catch((err: Error & { code?: string | number }) => {
        if (!cancelled) {
          const errorMessage =
            err?.message ||
            errorMsg.find((obj) => obj.key === 'sentiment')?.label ||
            null;
          setError(errorMessage);
          onErrorRef.current?.({
            message: 'Request failed with status code 401',
            type: 'AxiosError_ERR_BAD_REQUEST',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, ric]);

  return { data, isLoading, error };
};

export default useSentiment;
